import { inject, Injectable } from "@angular/core";
import { FormGroup, FormBuilder, FormArray } from "@angular/forms";

import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import {
  RulesetBooleanOperatorEnum,
  RulesetConditionOperatorEnum,
  RulesetResourceTypeEnum,
  TargetPathEnum,
} from "@shared/enums";
import {
  IConditionForm,
  IConditionGroups,
  IConditionItem,
  IConditionGroupForm,
  IConditionType,
  IConditionItemForm,
  IExpectation,
  IExpectationForm,
  IExpectationItemForm,
} from "@shared/interfaces/ruleset.interface";
import { CustomValidators } from "@shared/validators/custom-validators.validator";

@Injectable({
  providedIn: "root",
})
export class RulesetsBuilderService {
  private formBuilder: FormBuilder = inject(FormBuilder);

  documentTypeUrisInRuleset: string[] = [];

  locationTypeUrisInRuleset: string[] = [];

  locationUrisInRuleset: string[] = [];

  reset() {
    this.documentTypeUrisInRuleset = [];
    this.locationTypeUrisInRuleset = [];
    this.locationUrisInRuleset = [];
  }

  getPropertyOptions = (resourceTypeEnum: RulesetResourceTypeEnum): InputSelectOption[] => {
    switch (resourceTypeEnum) {
      case RulesetResourceTypeEnum.CONNECTION:
        return [
          { label: $localize`:@@ruleset.countries:Country(ies)`, value: TargetPathEnum.COUNTRY },
        ];
      case RulesetResourceTypeEnum.LOCATION:
        return [
          { label: $localize`:@@ruleset.types:Type(s)`, value: TargetPathEnum.TYPES },
          { label: $localize`:@@ruleset.countries:Country(ies)`, value: TargetPathEnum.COUNTRY },
        ];
      case RulesetResourceTypeEnum.DELIVERY:
        return [
          {
            label: $localize`:@@ruleset.fromlocationsexact:From location(s): Exact location(s)`,
            value: TargetPathEnum.FROM_LOCATION,
          },
          {
            label: $localize`:@@ruleset.fromlocationstypes:From location(s): Type(s)`,
            value: TargetPathEnum.FROM_TYPES,
          },
          {
            label: $localize`:@@ruleset.fromlocationscountries:From location(s): Country(ies)`,
            value: TargetPathEnum.FROM_COUNTRIES,
          },
          {
            label: $localize`:@@ruleset.tolocationsexact:To location(s): Exact location(s)`,
            value: TargetPathEnum.TO_LOCATION,
          },
          {
            label: $localize`:@@ruleset.tolocationstypes:To location(s): Type(s)`,
            value: TargetPathEnum.TO_TYPES,
          },
          {
            label: $localize`:@@ruleset.tolocationscountries:To location(s): Country(ies)`,
            value: TargetPathEnum.TO_COUNTRIES,
          },
        ];
      default:
        return [];
    }
  };

  isConditionPresent(condition: IConditionGroups | IConditionItem): boolean {
    if (!condition) {
      return false;
    }

    return (condition.and?.length || 0) > 0 || (condition.or?.length || 0) > 0;
  }

  buildDefaultConditionForm(): FormGroup<IConditionForm> {
    return this.formBuilder.group({
      booleanOperator: [RulesetBooleanOperatorEnum.AND],
      groupForms: this.formBuilder.array([]),
    }) as unknown as FormGroup<IConditionForm>;
  }

  buildDefaultConditionGroupForm(): FormGroup<IConditionGroupForm> {
    return this.formBuilder.group({
      booleanOperator: [RulesetBooleanOperatorEnum.AND],
      itemForms: this.formBuilder.array([this.buildDefaultConditionItemForm()]),
    }) as FormGroup<IConditionGroupForm>;
  }

  buildDefaultConditionItemForm(): FormGroup<IConditionItemForm> {
    return this.formBuilder.group({
      operator: [RulesetConditionOperatorEnum.EQ, CustomValidators.required],
      path: [null, CustomValidators.required],
      value: [null, CustomValidators.required],
    }) as FormGroup<IConditionItemForm>;
  }

  buildConditionItemForm(
    conditionValueForm: FormGroup<IConditionItemForm>,
  ): FormGroup<IConditionItemForm> {
    const conditionTypeValue = conditionValueForm.value;

    return this.formBuilder.group({
      operator: [conditionTypeValue.operator, CustomValidators.required],
      path: [conditionTypeValue.path, CustomValidators.required],
      value: [conditionTypeValue.value, CustomValidators.required],
    }) as FormGroup<IConditionItemForm>;
  }

  private getConditionGroupForm(
    conditionItem: IConditionItem,
    isReadOnly: boolean = false,
  ): FormGroup<IConditionGroupForm> {
    const booleanOperator: RulesetBooleanOperatorEnum = conditionItem.and
      ? RulesetBooleanOperatorEnum.AND
      : RulesetBooleanOperatorEnum.OR;

    const itemForms = conditionItem[booleanOperator].map((conditionType) =>
      this.getConditionTypeForm(conditionType, isReadOnly),
    );

    return this.formBuilder.group({
      booleanOperator: [booleanOperator],
      itemForms: this.formBuilder.array(itemForms),
    });
  }

  private getConditionTypeForm(
    conditionType: IConditionType,
    isReadOnly: boolean = false,
  ): FormGroup<IConditionItemForm> {
    let operator = Object.keys(conditionType)[0];
    const value =
      operator === RulesetConditionOperatorEnum.NOT
        ? conditionType[operator].any
        : conditionType[operator];

    operator =
      operator === RulesetConditionOperatorEnum.NOT ? RulesetConditionOperatorEnum.NONE : operator;

    this.addToExistingUris(value.path, value.value);

    return this.formBuilder.group({
      operator: [
        { value: operator as RulesetConditionOperatorEnum, disabled: isReadOnly },
        CustomValidators.required,
      ],
      path: [{ value: value.path, disabled: isReadOnly }, CustomValidators.required],
      value: [{ value: value.value, disabled: isReadOnly }, CustomValidators.required],
    });
  }

  private addToExistingUris(path: string, value: string | string[]): void {
    let property = this.locationTypeUrisInRuleset;

    if (path === "fromLocation" || path === "toLocation") {
      property = this.locationUrisInRuleset;
    }

    if (Array.isArray(value)) {
      property.push(...value);
    } else {
      property.push(value);
    }
  }

  getExpectationForm(expectations: IExpectation[]): FormGroup<IExpectationForm> {
    return this.formBuilder.group({
      expectationForms: this.formBuilder.array(
        expectations.map((expectation) => this.getExpectationItemForm(expectation)),
      ),
    });
  }

  buildDefaultExpectationForm(): FormArray<FormGroup<IExpectationItemForm>> {
    return this.formBuilder.array([this.buildDefaultExpectationItemForm()]);
  }

  buildDefaultExpectationItemForm(): FormGroup<IExpectationItemForm> {
    return this.formBuilder.group({
      pathPrefix: [TargetPathEnum.DOCUMENT, CustomValidators.required],
      pathSuffix: [TargetPathEnum.TYPE, CustomValidators.required],
      operator: [RulesetConditionOperatorEnum.EQ, CustomValidators.required],
      value: [null, CustomValidators.required],
    }) as FormGroup<IExpectationItemForm>;
  }

  private getExpectationItemForm(expectation: IExpectation): FormGroup<IExpectationItemForm> {
    const mainOperator = expectation.condition.and || expectation.condition.or;

    const operator = Object.keys(mainOperator)[0];

    return this.formBuilder.group({
      pathPrefix: [expectation.path, CustomValidators.required],
      pathSuffix: [mainOperator[operator].path, CustomValidators.required],
      operator: [operator as RulesetConditionOperatorEnum, CustomValidators.required],
      value: [mainOperator[operator].value, CustomValidators.required],
    });
  }

  getConditionForm(
    condition: IConditionGroups | IConditionItem,
    isReadOnly: boolean = false,
  ): FormGroup<IConditionForm> {
    if (!this.isConditionPresent(condition)) {
      return this.formBuilder.group({
        booleanOperator: [RulesetBooleanOperatorEnum.AND],
        groupForms: this.formBuilder.array([]),
      }) as unknown as FormGroup<IConditionForm>;
    }

    const booleanOperator: RulesetBooleanOperatorEnum = condition.and
      ? RulesetBooleanOperatorEnum.AND
      : RulesetBooleanOperatorEnum.OR;

    let isGroup: boolean = false;

    const keys = condition[booleanOperator].flatMap((condition) => Object.keys(condition));

    if (
      keys.includes(RulesetBooleanOperatorEnum.AND) ||
      keys.includes(RulesetBooleanOperatorEnum.OR)
    ) {
      isGroup = true;
    }

    let groupForms: FormGroup<IConditionGroupForm>[] = [];

    if (isGroup) {
      groupForms = condition[booleanOperator].map((condition) =>
        this.getConditionGroupForm(condition, isReadOnly),
      );
    } else {
      groupForms = [
        this.formBuilder.group({
          booleanOperator: [booleanOperator],
          itemForms: this.formBuilder.array(
            condition[booleanOperator].map((condition) =>
              this.getConditionTypeForm(condition, isReadOnly),
            ),
          ),
        }),
      ];
    }

    return this.formBuilder.group({
      booleanOperator: [booleanOperator],
      groupForms: this.formBuilder.array(groupForms),
    });
  }
}
